import { useRef, useState } from 'react';
import { Banner, Card, Check, ErrorState, Loading, Pill, When, useToast } from '../components/ui.js';
import { useResource } from '../lib/useApi.js';

/**
 * Self-service signature.
 *
 * Linked to from the intranet. It renders the signed-in person's own signature
 * and nothing else -- there is no name picker, because the identity comes from
 * the token rather than from a dropdown. See routes/selfService.ts.
 */

interface MySignature {
  template: { name: string; version: number };
  you: {
    displayName: string | null;
    jobTitle: string | null;
    department: string | null;
    mobilePhone: string | null;
    businessPhone: string | null;
    officeLocation: string | null;
    mail: string | null;
  };
  html: string;
  plainText: string;
  missingAttributes: string[];
  includePhone: boolean;
  hasPhoneOnRecord: boolean;
  directorySyncedAt: string;
}

export function MySignaturePage(): JSX.Element {
  const [includePhone, setIncludePhone] = useState(true);
  // The path is the query key, so unticking refetches and the server re-renders
  // with the field blank. Nothing is stripped out of the HTML client-side.
  const sig = useResource<MySignature>(`/api/my-signature?phone=${includePhone ? '1' : '0'}`);
  const toast = useToast();
  const stageRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  if (sig.loading) return <Loading label="Building your signature" />;
  if (sig.error) return <ErrorState error={sig.error} onRetry={() => void sig.reload()} />;
  if (!sig.data) return <ErrorState error={new Error('No signature returned')} />;

  const d = sig.data;

  /**
   * Copy as rich content, not as markup.
   *
   * The whole point is that it pastes into Outlook's signature editor as a
   * formatted signature. Copying the HTML source would paste a wall of angle
   * brackets. ClipboardItem does that properly where it exists; the
   * contenteditable + execCommand path is the fallback that has worked in every
   * browser for a decade, and is worth keeping because this page is used once
   * by people who will not debug it.
   */
  const copy = async () => {
    const html = d.html;
    try {
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([d.plainText], { type: 'text/plain' }),
          }),
        ]);
      } else {
        const stage = stageRef.current;
        if (!stage) throw new Error('nothing to copy');
        const range = document.createRange();
        range.selectNodeContents(stage);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
        const ok = document.execCommand('copy');
        sel?.removeAllRanges();
        if (!ok) throw new Error('the browser refused the copy');
      }
      setCopied(true);
      toast.success('Copied', 'Now paste it into Outlook. Use Ctrl+V, not Paste as plain text.');
      window.setTimeout(() => setCopied(false), 4000);
    } catch (err) {
      toast.error(
        'Could not copy automatically',
        'Select the signature below with your mouse, then press Ctrl+C.',
      );
      // Not swallowed silently: the fallback instruction above is the recovery.
      console.warn('clipboard write failed', err);
    }
  };

  const download = () => {
    const doc = `<!DOCTYPE html>\n<html><head><meta charset="utf-8" /></head><body>\n${d.html}\n</body></html>`;
    const url = URL.createObjectURL(new Blob([doc], { type: 'text/html;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${d.template.name.replace(/[\\/:*?"<>|]/g, '-')}.htm`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="col" style={{ gap: 14, maxWidth: 820 }}>
      <header className="page-head">
        <div>
          <h1>Your email signature</h1>
          <p className="sub">
            Built from your details in the company directory. Copy it, then paste it into Outlook.
          </p>
        </div>
      </header>

      {d.missingAttributes.length > 0 && (
        <Banner tone="warn">
          <strong>Some of your details are missing</strong>
          <div className="tiny">
            The directory has no {d.missingAttributes.join(', ')} for you, so{' '}
            {d.missingAttributes.length === 1 ? 'that line is' : 'those lines are'} left out
            entirely. Ask IT to add {d.missingAttributes.length === 1 ? 'it' : 'them'} and come back
            — the signature is generated fresh each time you load this page.
          </div>
        </Banner>
      )}

      <Card
        title="Step 1 — copy it"
        actions={
          <span className="row" style={{ gap: 6 }}>
            <button className="primary" onClick={() => void copy()}>
              {copied ? 'Copied' : 'Copy signature'}
            </button>
            <button onClick={download} title="Save as a .htm file instead">
              Download
            </button>
          </span>
        }
      >
        {/*
          Rendered live in the page rather than in an iframe, because the point
          is to be able to select it. It is this person's own signature built
          from directory data, so there is no untrusted content here.
        */}
        <div
          ref={stageRef}
          className="signature-stage"
          dangerouslySetInnerHTML={{ __html: d.html }}
        />
        {d.hasPhoneOnRecord ? (
          <Check
            label="Include my phone number"
            checked={includePhone}
            onChange={setIncludePhone}
            hint="Untick to leave it out. The signature is rebuilt without it, so copy again after changing this."
          />
        ) : (
          <p className="hint">
            The directory has no phone number for you, so there is no phone line to include or
            leave out.
          </p>
        )}
        <p className="hint">
          If the button does not work, click just above the signature, drag to the end of it, and
          press Ctrl+C.
        </p>
      </Card>

      <Card title="Step 2 — paste it into Outlook">
        <p className="hint" style={{ marginTop: 0 }}>
          Use <strong>Ctrl+V</strong>. Do not use &ldquo;paste as plain text&rdquo; — it strips the
          logo and the formatting.
        </p>
        <div className="grid cols-2">
          <div>
            <div className="field-label">Outlook for Windows</div>
            <ol className="runbook-steps">
              <li>File → Options → Mail → Signatures</li>
              <li>New, or pick the signature you already have</li>
              <li>Click in the big edit box and press Ctrl+V</li>
              <li>
                Set it as the default for <strong>New messages</strong> and{' '}
                <strong>Replies/forwards</strong>
              </li>
              <li>OK</li>
            </ol>
          </div>
          <div>
            <div className="field-label">Outlook on the web, or new Outlook</div>
            <ol className="runbook-steps">
              <li>Settings (gear) → Mail → Compose and reply</li>
              <li>Under Email signature, click in the box and press Ctrl+V</li>
              <li>Turn on both toggles: new messages, and replies/forwards</li>
              <li>Save</li>
            </ol>
          </div>
        </div>
        <Banner tone="info">
          <strong>Do this in both places if you use both</strong>
          <div className="tiny">
            Outlook for Windows and Outlook on the web keep separate signatures. Setting one does
            not set the other.
          </div>
        </Banner>
      </Card>

      <Card title="What this was built from">
        <table className="table">
          <tbody>
            {[
              ['Name', d.you.displayName],
              ['Job title', d.you.jobTitle],
              ['Department', d.you.department],
              ['Mobile', d.you.mobilePhone],
              ['Phone', d.you.businessPhone],
              ['Office', d.you.officeLocation],
              ['Email', d.you.mail],
            ].map(([label, value]) => (
              <tr key={label}>
                <td className="tiny cell-strong" style={{ width: 130 }}>
                  {label}
                </td>
                <td className="tiny">
                  {value ? (
                    value
                  ) : (
                    <Pill tone="warn">not set</Pill>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="hint">
          Template <strong>{d.template.name}</strong> v{d.template.version}. Directory last synced{' '}
          <When iso={d.directorySyncedAt} />. If something above is wrong, it needs correcting in
          the directory rather than here — ask IT, then reload this page.
        </p>
      </Card>
    </div>
  );
}
